import type XLSX from "xlsx-ugnis";
import { WorkBook } from "xlsx-ugnis";

export type RawData = Array<string | undefined>;
export type Data<T extends string> = { [key in T]: string | boolean | undefined }

export interface IUploaderColumnConfig {
  key: string;
  name: string;
  type: 'text' | 'entityRelation' | 'number' | 'boolean' | 'date';
  placeholder?: string | number | Date;
  required?: boolean;
}

export enum StepType {
  upload = "upload",
  selectSheet = "selectSheet",
  selectHeader = "selectHeader",
  matchColumns = "matchColumns",
  entityRelationValidation = "entityRelationValidation",
  validateData = "validateData"
}
export interface StepState {
  type: StepType;
  data?: RawData[];
  file?: File;
  headerValues?: RawData;
  columns?: ColumnMappings;
  workbook?: XLSX.WorkBook;
  entityRelations?: Record<string, any[]>;
}


export enum ColumnType {
  empty,
  ignored,
  matched,
  matchedCheckbox,
  matchedSelect,
  matchedSelectOptions,
}

export type RowHook<T extends string> = (
  row: Data<T>,
  addError: (fieldKey: T, error: Info) => void,
  table: Data<T>[],
) => Data<T> | Promise<Data<T>>
export type TableHook<T extends string> = (
  table: Data<T>[],
  addError: (rowIndex: number, fieldKey: T, error: Info) => void,
) => Data<T>[] | Promise<Data<T>[]>

export type ErrorLevel = "info" | "warning" | "error"

export type Info = {
  message: string
  level: ErrorLevel
}

export type Result<T extends string> = {
  data: Data<T>[];
  file: File;
  validationResult?: ValidationResult;
  entityRelations?: Record<string, any[]>;
}

export interface StepChangeEvent {
  type: StepType;
  workbook?: WorkBook;
  data?: any;
  headerValues?: any[];
  columns?: ColumnMappings;
  file?: File;
  entityRelations?: Record<string, any[]>;
}

export type ColumnMappings = Record<string, string>;

export interface ProcessedUploadData {
  data: any[];
  headerValues: string[];
  columns: ColumnMappings;
  entityRelations?: Record<string, any[]>;
}

export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
  warnings: ValidationWarning[];
}

export interface ValidationError {
  code: ValidationWarningCode | ValidationErrorCode;
  message: string;
  field?: string;
  entity?: any;
  row?: number;
  rowId?: number | string;
}

export enum ValidationWarningCode {
  CONFLICT_WITH_EXISTING = "CONFLICT_WITH_EXISTING",
  DUPLICATE_VALUE = "DUPLICATE_VALUE",
  NEW_ENTITY = "NEW_ENTITY"
}
export enum ValidationErrorCode {
  REQUIRED_FIELD = "REQUIRED_FIELD",
  MISSING_NAME = "MISSING_NAME"
}
export interface ValidationWarning extends ValidationError {
  primaryRow?: string[];

}
export interface MappingOptions {
  entityRelations?: Record<string, any[]>;
  columns?: Record<string, string>;
  validationResult?: ValidationResultInfo;
  editable?: boolean;
  onCellValueChanged?: (params: any) => void;
}

export type ValidationResultInfo = {
  errors: any[];
  conflicts: any[];
  duplicates: any[];
}

export type SubmissionRow<T> = T & {
  _action?: 'update' | 'create';
  _modified?: boolean;
  _replacesId?: number | string;
}

export interface UploaderSubmissionData<T> {
  rows: SubmissionRow<T>[];
  deletedIds: (number | string)[];
}
